import axios from "axios";
import { isConsentRequiredResponse, triggerConsentGate } from "../utils/consentGate";
import {
  TERMINAL_AUTH_CODES,
  TRANSIENT_AUTH_CODES,
  classifyAuthFailure,
} from "./authCodes";
import {
  getClockSkewMs,
  isRefreshTokenExpired,
  recordFreshAccessToken,
} from "../utils/jwtClock";
import { recordLastLogoutCode } from "../utils/sessionRevocation";

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:4152/api/v1").replace(/\/+$/, '');
const API_ORIGIN = API_URL.replace(/\/api\/v1$/, '');
//console.log("API_URL,", API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Log API URL in development
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_URL);
}

// ───────────────────────────────────────────────────────────────────────────
// Single-flight refresh mutex.
//
// When the 24h access token expires, every in-flight request 401s at roughly
// the same moment. Without a mutex each one fires its OWN POST /auth/refresh in
// parallel; because the backend ROTATES the refresh token on each call, the
// concurrent rotations race and last-writer-wins in localStorage — some retries
// then carry a stale token, 401 again, and hit the hard-logout in the catch
// block ("Your session has expired. Please login again."). This is the primary
// remaining cause of spurious web logouts.
//
// Fix: exactly ONE /auth/refresh is in flight at any time. All concurrent 401s
// await the same shared promise and then retry with the single resolved token.
// (Mirrors the mobile client's attemptTokenRefresh pattern.)
// ───────────────────────────────────────────────────────────────────────────
let isRefreshing = false;
let refreshPromise = null;

/** Body code of the last failed refresh (used as the stored logout reason). */
let lastRefreshFailureCode = null;

/**
 * Canonical terminal auth codes (permanent-session-continuity design §6.1),
 * re-exported by name for the parity tests. Single source: `./authCodes`.
 */
export { TERMINAL_AUTH_CODES };

// ───────────────────────────────────────────────────────────────────────────
// X-Auth-Contract capability flag (design §4.5 Web, FR-5.5).
// The new X-Client-* / X-Last-Logout-Reason headers are sent ONLY while the most
// recent response advertised `X-Auth-Contract`. Self-correcting both ways, so a
// backend rollback with tabs open never breaks CORS for more than one request.
// ───────────────────────────────────────────────────────────────────────────
let authContractSupported = false;
let authContractProbeDone = false;

const CLIENT_META_HEADERS = ['X-Client-Platform', 'X-Client-Build', 'X-Last-Logout-Reason'];
const REASON_CODE_RE = /^[A-Z0-9_]{1,64}$/;

/** Read one response header from AxiosHeaders or a plain object. Never throws. */
function readResponseHeader(headers, name) {
  try {
    if (!headers) return undefined;
    const viaGet = typeof headers.get === 'function' ? headers.get(name) : undefined;
    if (viaGet !== undefined && viaGet !== null && viaGet !== false) return viaGet;
    return headers[name] ?? headers[String(name).toLowerCase()];
  } catch (e) {
    console.warn('[api:authContract] header read failed:', e?.message || e);
    return undefined;
  }
}

/**
 * Update the contract flag from a response (true iff it carries X-Auth-Contract).
 * Returns the new flag value. Never throws.
 */
export function updateAuthContractFlag(response) {
  try {
    authContractSupported = !!readResponseHeader(response?.headers, 'x-auth-contract');
  } catch (e) {
    console.warn('[api:authContract] update failed:', e?.message || e);
    authContractSupported = false;
  }
  return authContractSupported;
}

/** Current flag value (for tests / diagnostics). */
export function isAuthContractSupported() {
  return authContractSupported;
}

/** The client meta headers to attach (only call when the flag is on). */
function buildClientMetaHeaders() {
  const h = { 'X-Client-Platform': 'web' };
  if (process.env.REACT_APP_BUILD_NUMBER) h['X-Client-Build'] = process.env.REACT_APP_BUILD_NUMBER;
  return h;
}

/** Remove the client meta headers from a config's headers (AxiosHeaders or plain). */
function stripClientMetaHeaders(headers) {
  try {
    if (!headers) return;
    for (const name of CLIENT_META_HEADERS) {
      try {
        if (typeof headers.delete === 'function') headers.delete(name);
      } catch (e) {
        console.warn('[api:authContract] header delete failed:', e?.message || e);
      }
      delete headers[name];
      delete headers[name.toLowerCase()];
    }
  } catch (e) {
    console.warn('[api:authContract] header strip failed:', e?.message || e);
  }
}

/**
 * Learn X-Auth-Contract support from `/api/health` once per page load (or again
 * when `force`). Never throws; resolves with the flag value.
 */
export async function ensureAuthContractKnown({ force = false } = {}) {
  try {
    if (!force && (authContractSupported || authContractProbeDone)) return authContractSupported;
    authContractProbeDone = true;
    if (typeof axios.get !== 'function') return authContractSupported;
    try {
      const res = await axios.get(`${API_ORIGIN}/api/health`, { timeout: 2000 });
      updateAuthContractFlag(res);
    } catch (e) {
      if (e?.response) updateAuthContractFlag(e.response);
      else console.info('[api:authContract] health probe failed:', e?.message || e);
    }
  } catch (e) {
    console.warn('[api:authContract] ensureAuthContractKnown failed:', e?.message || e);
  }
  return authContractSupported;
}

/** `{ 'X-Last-Logout-Reason': code }` when allowed, otherwise `{}`. Never throws. */
export function buildLastLogoutReasonHeaders() {
  try {
    if (!authContractSupported) return {};
    const code = localStorage.getItem('lastLogoutCode');
    if (typeof code === 'string' && REASON_CODE_RE.test(code)) {
      return { 'X-Last-Logout-Reason': code };
    }
  } catch (e) {
    console.warn('[api] buildLastLogoutReasonHeaders failed:', e?.message || e);
  }
  return {};
}

/**
 * Pure classifier for a `POST /auth/refresh` attempt (design §6.4, FR-10.1,
 * normative; identical to MyAMSMobile `classifyRefreshOutcome`).
 * Only 401/403, a terminal code, or an already-expired refresh token is terminal.
 * Total and never throws.
 *
 * @param {{ hasResponse?: boolean, status?: number|null, code?: string|null,
 *           accessToken?: string|null, refreshTokenExpired?: boolean }} [input]
 * @returns {{ status: 'success', accessToken: string } | { status: 'transient' } | { status: 'terminal' }}
 */
// input: { hasResponse, status, code, accessToken, refreshTokenExpired }
export function classifyRefreshOutcome(input) {
  const hasResponse = input?.hasResponse === true;
  const status = input?.status;
  const code = typeof input?.code === 'string' ? input.code : '';
  const accessToken = input?.accessToken;

  // 1. Success iff a non-empty access token was extracted.
  if (typeof accessToken === 'string' && accessToken.length > 0) return { status: 'success', accessToken };
  // 2. FR-10.2: the stored refresh token has already expired (skew-adjusted, 60 s tolerance). Retrying cannot help.
  if (input?.refreshTokenExpired === true) return { status: 'terminal' };
  // 2b. Kept in place from today's classifier: no response, or a gateway/unavailable status, is transient
  //     whatever the body says. A 502/503/504 page cannot carry a backend auth decision.
  if (!hasResponse) return { status: 'transient' };
  if (status === 502 || status === 503 || status === 504) return { status: 'transient' };
  // 3. Genuine auth rejection.
  if (status === 401 || status === 403) return { status: 'terminal' };
  // 4. Genuine revocation carried in the body (includes USER_INACTIVE on the 404 refresh emitter, A17).
  if (code && TERMINAL_AUTH_CODES.includes(code)) return { status: 'terminal' };
  // Transient-code arm (AUTH_CHECK_FAILED, SERVICE_STARTING). Subsumed by arm 5, kept for readability.
  if (code && TRANSIENT_AUTH_CODES.includes(code)) return { status: 'transient' };
  // 5. Everything else: any other 5xx (coded or not), 404/408/425/429 without a terminal code,
  //    a non-JSON or unparseable body, a 2xx without a usable token (captive portal).
  return { status: 'transient' };
}

/** FR-10.6 breadcrumb: status + code only, never a token / URL / body. */
function logRefreshTransientBreadcrumb(status, code) {
  try {
    const s = typeof status === 'number' && Number.isFinite(status) ? String(status) : '-';
    const c = typeof code === 'string' && REASON_CODE_RE.test(code) ? code : '-';
    console.info(`[auth] refresh transient: status=${s} code=${c}`);
  } catch (e) {
    console.warn('[auth] refresh breadcrumb failed:', e?.message || e);
  }
}

/** Clear credentials and redirect to login exactly once, with a reason message. */
function forceWebLogout(reason, code) {
  if (code) recordLastLogoutCode(code);
  localStorage.setItem('logoutReason', reason);
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('selectedInstitute');
  window.location.href = '/login';
}

/**
 * Perform a single token refresh. Concurrent callers share one in-flight
 * promise. Resolves to a three-valued `RefreshResult`:
 *   `{ status: 'success', accessToken }` | `{ status: 'transient' }` | `{ status: 'terminal' }`
 *
 * Outcome is decided by the pure `classifyRefreshOutcome(...)` (design §6.4).
 * Never throws — callers branch on `result.status`.
 */
function performTokenRefresh() {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    let refreshToken = null;
    try {
      lastRefreshFailureCode = null;
      refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No stored refresh token ⇒ cannot refresh ⇒ must log out (terminal).
        // Web has no hydration phase (design §4.9), so this stays terminal.
        return { status: 'terminal' };
      }

      // FR-10.2 pre-check: an already-expired refresh token cannot succeed; skip the network call.
      if (isRefreshTokenExpired(refreshToken, Date.now(), getClockSkewMs())) {
        lastRefreshFailureCode = 'REFRESH_EXPIRED';
        return { status: 'terminal' };
      }

      const refreshUrl = `${API_URL}/auth/refresh`;
      const postRefresh = (withMeta) =>
        axios.post(
          refreshUrl,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${refreshToken}`,
              ...(withMeta ? buildClientMetaHeaders() : {}),
            },
          }
        );

      let response;
      const sentMeta = authContractSupported;
      try {
        response = await postRefresh(sentMeta);
      } catch (firstErr) {
        if (!firstErr?.response && sentMeta) {
          // Possibly a CORS preflight rejection after a backend rollback: drop the
          // new headers and retry once (refresh is safe to repeat; no reuse detection).
          authContractSupported = false;
          void ensureAuthContractKnown({ force: true });
          response = await postRefresh(false);
        } else {
          throw firstErr;
        }
      }

      updateAuthContractFlag(response);

      // Backend wraps in { status, message, data: { accessToken, token, refreshToken } }
      const newAccessToken =
        response?.data?.data?.accessToken ||
        response?.data?.data?.token ||
        response?.data?.accessToken ||
        response?.data?.token;

      if (typeof newAccessToken === 'string' && newAccessToken) {
        localStorage.setItem('accessToken', newAccessToken);
        // Persist the rotated refresh token (sliding window). If the backend didn't
        // return one (older backend), keep the existing token.
        const rotatedRefreshToken =
          response?.data?.data?.refreshToken || response?.data?.refreshToken;
        if (typeof rotatedRefreshToken === 'string' && rotatedRefreshToken) {
          localStorage.setItem('refreshToken', rotatedRefreshToken);
        }
        recordFreshAccessToken(newAccessToken);
        try {
          window.dispatchEvent(new CustomEvent('myams:access-token-refreshed'));
        } catch (evErr) {
          console.warn('[api] access-token-refreshed dispatch failed:', evErr?.message || evErr);
        }
      }

      const code = response?.data?.data?.code || response?.data?.code;
      const result = classifyRefreshOutcome({
        hasResponse: true,
        status: response?.status,
        code,
        accessToken: newAccessToken,
        refreshTokenExpired: isRefreshTokenExpired(refreshToken, Date.now(), getClockSkewMs()),
      });
      if (result.status !== 'success') {
        lastRefreshFailureCode = typeof code === 'string' && code ? code : null;
      }
      if (result.status === 'transient') logRefreshTransientBreadcrumb(response?.status, code);
      return result;
    } catch (e) {
      // Never rethrow. Classify from the axios error (design §6.4).
      if (e?.response) updateAuthContractFlag(e.response);
      const code = e?.response?.data?.data?.code || e?.response?.data?.code;
      lastRefreshFailureCode = typeof code === 'string' && code ? code : null;
      const result = classifyRefreshOutcome({
        hasResponse: !!e?.response,
        status: e?.response?.status,
        code,
        accessToken: null,
        refreshTokenExpired: isRefreshTokenExpired(refreshToken, Date.now(), getClockSkewMs()),
      });
      if (result.status === 'transient' && e?.response) {
        logRefreshTransientBreadcrumb(e.response.status, code);
      }
      return result;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Single-flight refresh for proactive / socket callers. Never throws. */
export function refreshAccessTokenSingleFlight() {
  return performTokenRefresh();
}

// ───────────────────────────────────────────────────────────────────────────
// FR-10.3 stale-request guard (design §4.9 Web, normative).
// ───────────────────────────────────────────────────────────────────────────
function readRequestBearer(config) {
  try {
    const h = config?.headers;
    const v = (typeof h?.get === 'function' ? h.get('Authorization') : undefined) ?? h?.Authorization ?? h?.authorization;
    return typeof v === 'string' ? v : '';
  } catch (e) { console.warn('[api:staleGuard] header read failed:', e?.message || e); return ''; }
}
/** True when the failed request carried a token that is no longer localStorage.accessToken (and one exists). */
function isStaleAuthRequest(config) {
  try {
    const current = localStorage.getItem('accessToken');
    if (!current) return false;                          // logged out: normal path
    return readRequestBearer(config) !== `Bearer ${current}`;
  } catch (e) { console.warn('[api:staleGuard] check failed:', e?.message || e); return false; }
}

/**
 * When the request is stale: retry once with the current token, or reject if it
 * was already retried. Returns null when the request is not stale. Never logs out.
 */
function handleStaleAuthRequest(originalRequest, error) {
  if (!isStaleAuthRequest(originalRequest)) return null;
  if (!originalRequest._staleRetry) {
    originalRequest._staleRetry = true;
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;
    return api(originalRequest);                         // the request interceptor re-reads the same token
  }
  return Promise.reject(error);                          // already retried once: surface, never log out
}

const AUTH_ENDPOINT_FRAGMENTS = [
  '/login',
  '/forgot-password',
  '/verify-otp',
  '/reset-password',
  '/resend-otp',
  '/auth/qr/session',
  '/auth/refresh',
];

/** User-facing copy for a coded terminal logout (design §4.3c). */
function terminalLogoutMessage(code) {
  if (code === 'REFRESH_EXPIRED' || code === 'REFRESH_INVALID' || code === 'REFRESH_TOKEN_MISSING') {
    return 'Your session has expired. Please login again.';
  }
  if (code === 'USER_INACTIVE') {
    return 'Your account is no longer active. Please contact your institute.';
  }
  if (code === 'DEVICE_APPROVAL_PENDING') {
    return 'This device needs approval. Please sign in again.';
  }
  return 'Your session has been revoked. Please login again.';
}

/** The existing terminal side effects (deferred redirect). */
function terminalLogout(userMsg, code) {
  recordLastLogoutCode(code);
  localStorage.setItem('logoutReason', userMsg);
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('selectedInstitute');
  setTimeout(() => { window.location.href = '/login'; }, 50);
}

function triggerConsentFromError(error) {
  const data = error.response?.data || {};
  triggerConsentGate({
    consentRequired: true,
    activeConsentForm: data.activeConsentForm || null,
    message:
      data.message ||
      "A new privacy policy requires your acceptance. Please review and accept it to continue.",
  });
}

/**
 * The 401 refresh block, shared by the coded and legacy paths.
 * Caller has already checked `status === 401 && !_retry && !excluded`.
 */
async function refreshAndRetry(error, originalRequest) {
  originalRequest._retry = true;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    const stale = handleStaleAuthRequest(originalRequest, error);
    if (stale) return stale;
    forceWebLogout('You are not logged in. Please login to continue.', 'CLIENT_NO_REFRESH_TOKEN');
    return Promise.reject(error);
  }

  // Await the SINGLE shared refresh. Concurrent 401s all resolve against the
  // same in-flight call, so no parallel /auth/refresh rotations can race.
  const refresh = await performTokenRefresh();

  if (refresh?.status === 'success') {
    // Retry the original request with the freshly resolved token (unchanged behavior).
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${refresh.accessToken}`;
    return api(originalRequest);
  } else if (refresh?.status === 'transient') {
    // Transient refresh failure: keep the session alive; tokens untouched, no
    // logoutReason, no redirect. Reject the ORIGINAL error.
    try {
      console.info('[auth] refresh deferred: network unavailable');
    } catch (logErr) {
      console.warn('[auth] breadcrumb failed:', logErr?.message || logErr);
    }
    return Promise.reject(error);
  }

  // 'terminal' (or any unexpected outcome). A login in this tab or another one may
  // have replaced the token while the refresh was in flight (FR-10.3).
  const stale = handleStaleAuthRequest(originalRequest, error);
  if (stale) return stale;
  forceWebLogout(
    'Your session has expired. Please login again.',
    lastRefreshFailureCode || 'CLIENT_REFRESH_TERMINAL'
  );
  return Promise.reject(error);
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Client meta headers only while the backend advertises X-Auth-Contract (A2, FR-5.5).
    try {
      if (authContractSupported && !config.__metaRetry) {
        config.headers = config.headers || {};
        const meta = buildClientMetaHeaders();
        for (const k of Object.keys(meta)) config.headers[k] = meta[k];
        config.__sentClientMeta = true;
      } else {
        config.__sentClientMeta = false;
      }
    } catch (metaErr) {
      console.warn('[api] client meta headers failed:', metaErr?.message || metaErr);
    }

    // If data is FormData, remove Content-Type header so axios can set multipart/form-data with boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    
    if (process.env.NODE_ENV === 'development') {
      const loggedUrl = `${String(config.baseURL).replace(/\/+$/, '')}/${String(config.url).replace(/^\/+/, '')}`;
      console.log('API Request:', {
        url: loggedUrl,
        method: config.method,
        hasToken: !!token,
        // NOTE: for GET requests, payload is in `params` (query string), not `data`
        params: config.params ?? {},
        data: config.data
      });
    }
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    updateAuthContractFlag(response);
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  async (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.message,
        responseData: error.response?.data
      });
    }

    const originalRequest = error.config || {};

    if (error.response) {
      updateAuthContractFlag(error.response);
    } else if (originalRequest.__sentClientMeta && !originalRequest.__metaRetry) {
      // ─── Network failure on a request that carried the new headers (FR-5.5) ───
      // Could be a CORS preflight rejection after a backend rollback. Stop sending
      // the headers; retry idempotent requests once. Mutations are NOT retried
      // (double-submit risk) and reject as an ordinary transient error.
      authContractSupported = false;
      stripClientMetaHeaders(originalRequest.headers);
      originalRequest.__metaRetry = true;
      void ensureAuthContractKnown({ force: true });
      const method = String(originalRequest.method || 'get').toLowerCase();
      if (method === 'get' || method === 'head' || method === 'options') {
        return api(originalRequest);
      }
    }

    const status = error.response?.status;
    const code = typeof error.response?.data?.code === 'string' ? error.response.data.code : '';

    // ─── Transient server errors (503, 502, network timeout, transient codes) ───
    // Do NOT force-logout on these — the server is restarting or temporarily unavailable.
    if (!error.response || status === 502 || status === 503 || status === 504 ||
        TRANSIENT_AUTH_CODES.includes(code)) {
      return Promise.reject(error);
    }

    const url = originalRequest.url || '';
    const isAuthEndpoint = AUTH_ENDPOINT_FRAGMENTS.some((frag) => url.includes(frag));
    // Don't auto-redirect for change password / auth endpoints - let them handle errors locally
    const isChangePasswordEndpoint = url.includes('/change-password') ||
      url.includes('/login') ||
      url.includes('/forgot-password') ||
      url.includes('/profile/change-password');

    // ─── FR-10.3 stale-request guard: a failure for a non-current token never logs out ───
    if (!isAuthEndpoint && (status === 401 || TERMINAL_AUTH_CODES.includes(code))) {
      const stale = handleStaleAuthRequest(originalRequest, error);
      if (stale) return stale;
    }

    // ─── Coded path (new backend, design §4.3c) ───
    if (code) {
      const cls = classifyAuthFailure({ status, code });

      if (cls === 'terminal' && !isAuthEndpoint) {
        terminalLogout(terminalLogoutMessage(code), code);
        return Promise.reject(error);
      }

      if (isConsentRequiredResponse(error) || code === 'CONSENT_REQUIRED') {
        triggerConsentFromError(error);
        return Promise.reject(error);
      }

      if (cls === 'refresh' && status === 401 && !originalRequest._retry &&
          !isChangePasswordEndpoint && !isAuthEndpoint) {
        return refreshAndRetry(error, originalRequest);
      }

      if (code === 'TOKEN_MISSING') {
        if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
          forceWebLogout('You are not logged in. Please login to continue.', 'TOKEN_MISSING');
        }
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }

    // ─── Legacy path (no `code`: old backend) — unchanged behaviour ───

    // Terminal auth errors — these indicate the session is irrecoverably dead
    // (token was never provided, session was explicitly revoked by admin/device-approval).
    // Only force logout for truly terminal cases; expired tokens should attempt refresh first.
    const respMsg = error.response?.data?.message || error.response?.data?.error || '';
    const respCode = error.response?.data?.code || '';
    const terminalCodes = TERMINAL_AUTH_CODES;
    const isTerminalAuthError = terminalCodes.includes(respCode) || (respMsg && (
      /token not provided/i.test(respMsg) ||
      /session revoked/i.test(respMsg)
    ));
    if (isTerminalAuthError) {
      const userMsg = /token not provided/i.test(respMsg)
        ? 'You have been logged out. Please login to continue.'
        : 'Your session has been revoked. Please login again.';
      terminalLogout(userMsg, /token not provided/i.test(respMsg) ? 'TOKEN_MISSING' : 'SESSION_REVOKED');
      return Promise.reject(error);
    }

    // Consent gate — stay signed in; redirect to consent screen via Redux handler.
    if (isConsentRequiredResponse(error)) {
      triggerConsentFromError(error);
      return Promise.reject(error);
    }

    // Other permission denials must not trigger token refresh / logout.
    if (status === 403) {
      return Promise.reject(error);
    }

    // If the original request was a permission denial disguised as 401, do not force logout.
    const isPermissionDenied =
      status === 401 &&
      respMsg &&
      (/unauthorized access/i.test(respMsg) || /forbidden/i.test(respMsg) || /permission/i.test(respMsg));

    if (isPermissionDenied) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry && !isChangePasswordEndpoint) {
      return refreshAndRetry(error, originalRequest);
    }

    return Promise.reject(error);
  }
);

export default api;
